import { useEffect, useState } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Inbox as InboxIcon,
  ShieldCheck,
  Calendar as CalendarIcon,
  Pencil,
  Settings as SettingsIcon,
  LogOut,
  Building2,
  Send as SendIcon,
  ChevronLeft,
  ChevronRight,
  Sun,
  Moon,
} from 'lucide-react'

import { getMe } from '../lib/api'
import { signOut } from '../lib/auth'

interface RailItem {
  to: string
  label: string
  Icon: typeof InboxIcon
  visible?: (isAdmin: boolean) => boolean
}

type Theme = 'light' | 'dark'

function initialTheme(): Theme {
  if (typeof window === 'undefined') return 'light'
  const saved = localStorage.getItem('plustag-theme') as Theme | null
  if (saved === 'light' || saved === 'dark') return saved
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

export default function Sidebar() {
  const navigate = useNavigate()
  const [expanded, setExpanded] = useState(false)
  const [theme, setTheme] = useState<Theme>(initialTheme)

  useEffect(() => {
    localStorage.setItem('plustag-theme', theme)
  }, [theme])

  // Clean up the global .dark class that an earlier deploy applied to <html>
  // — the theme is now scoped to the sidebar only.
  useEffect(() => {
    document.documentElement.classList.remove('dark')
  }, [])

  const { data: me } = useQuery({ queryKey: ['me'], queryFn: getMe })
  const isAdmin = (me?.roles || []).map(r => r.toLowerCase()).includes('admin')
  const myMailbox = me?.email || me?.sub || 'me'

  const addresses = (me?.addresses || []).length ? (me!.addresses as string[]) : [myMailbox]

  const items: RailItem[] = [
    { to: `/inbox/${encodeURIComponent(addresses[0])}`, label: 'Inbox',    Icon: InboxIcon },
    { to: `/sent/${encodeURIComponent(addresses[0])}`,  label: 'Sent',     Icon: SendIcon },
    { to: '/inbox/dmarc@plustag.email', label: 'DMARC mail', Icon: ShieldCheck, visible: (a) => a },
    { to: '/calendar',                  label: 'Calendar', Icon: CalendarIcon },
    { to: '/dmarc',                     label: 'DMARC reports', Icon: ShieldCheck, visible: (a) => a },
    { to: '/admin',                     label: 'Admin — Tenants', Icon: Building2, visible: (a) => a },
    { to: '/settings',                  label: 'Settings', Icon: SettingsIcon },
  ]

  return (
    <aside
      className={`
        ${theme === 'dark' ? 'dark' : ''}
        relative m-4 rounded-2xl bg-surface shadow-lift border border-line
        flex flex-col py-3
        transition-all duration-200
        ${expanded ? 'w-60 px-3' : 'w-20 px-2'}
      `}
    >
      {/* Hang-off chevron toggle: half-overlaps the right edge, stays within
          the canvas gap so it never covers the Compose window / message pane. */}
      <button
        onClick={() => setExpanded(v => !v)}
        aria-label={expanded ? 'Collapse menu' : 'Expand menu'}
        aria-expanded={expanded}
        title={expanded ? 'Collapse menu' : 'Expand menu'}
        className="
          absolute top-6 -right-3 z-10
          w-7 h-7 rounded-full
          bg-raised border border-line shadow-soft
          grid place-items-center
          text-pulse hover:text-ink-900 hover:shadow-lift
          transition-shadow
        "
      >
        {expanded ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
      </button>

      {/* Brand — navigates home */}
      <button
        onClick={() => navigate('/')}
        className={`
          font-semibold tracking-tight text-ink-900
          rounded-md hover:bg-canvas mb-3
          ${expanded ? 'text-base px-2 py-1 self-start' : 'text-xs px-1 py-1 w-full text-center'}
        `}
        aria-label="Workspace home"
        title="Workspace home"
      >
        {expanded ? '+Workspace' : 'Wkspc'}
      </button>

      {/* Compose */}
      <button
        onClick={() => navigate('/compose')}
        className={`
          rounded-soft bg-canvas shadow-soft grid text-ink-700 hover:shadow-lift transition-shadow
          ${expanded
            ? 'h-10 grid-cols-[auto_1fr] items-center gap-2 px-3 text-sm font-medium'
            : 'w-11 h-11 place-items-center mx-auto'}
        `}
        aria-label="Compose"
        title="Compose"
      >
        <Pencil size={18} strokeWidth={1.75} />
        {expanded && <span>Compose</span>}
      </button>

      <div className={`h-px bg-line my-2 ${expanded ? 'w-full' : 'w-8 self-center'}`} />

      {/* Nav */}
      <nav className="flex flex-col gap-1">
        {items.filter(i => !i.visible || i.visible(isAdmin)).map(i => (
          <NavLink
            key={i.to}
            to={i.to}
            title={i.label}
            className={({ isActive }) => `
              rounded-soft transition-all
              ${expanded
                ? `h-10 grid grid-cols-[auto_1fr] items-center gap-3 px-3 text-sm ${
                    isActive
                      ? 'bg-canvas text-ink-900 font-medium shadow-soft'
                      : 'text-ink-700 hover:bg-canvas/60 hover:text-ink-900'
                  }`
                : `w-11 h-11 grid place-items-center mx-auto ${
                    isActive
                      ? 'bg-canvas shadow-soft text-ink-900'
                      : 'text-ink-500 hover:text-ink-900 hover:bg-canvas/60'
                  }`}
            `}
          >
            <i.Icon size={18} strokeWidth={1.75} />
            {expanded && <span className="truncate">{i.label}</span>}
          </NavLink>
        ))}
      </nav>

      {/* Theme toggle — segmented pill when expanded, single icon button when collapsed */}
      <div className="mt-auto pt-3">
        {expanded ? (
          <div
            role="group"
            aria-label="Colour theme"
            className="grid grid-cols-2 rounded-pill bg-canvas p-1 text-xs font-medium"
          >
            <button
              onClick={() => setTheme('light')}
              aria-pressed={theme === 'light'}
              className={`
                rounded-pill h-7 inline-flex items-center justify-center gap-1.5 transition-colors
                ${theme === 'light' ? 'bg-raised shadow-soft text-ink-900' : 'text-ink-500 hover:text-ink-900'}
              `}
            >
              <Sun size={12} /> Light
            </button>
            <button
              onClick={() => setTheme('dark')}
              aria-pressed={theme === 'dark'}
              className={`
                rounded-pill h-7 inline-flex items-center justify-center gap-1.5 transition-colors
                ${theme === 'dark' ? 'bg-raised shadow-soft text-ink-900' : 'text-ink-500 hover:text-ink-900'}
              `}
            >
              <Moon size={12} /> Dark
            </button>
          </div>
        ) : (
          <button
            onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
            aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            title={theme === 'dark' ? 'Light mode' : 'Dark mode'}
            className="w-11 h-11 mx-auto rounded-soft grid place-items-center text-ink-500 hover:text-ink-900 hover:bg-canvas transition-colors"
          >
            {theme === 'dark' ? <Sun size={17} strokeWidth={1.75} /> : <Moon size={17} strokeWidth={1.75} />}
          </button>
        )}
      </div>

      {/* User + sign-out */}
      <div className={`pt-2 flex ${expanded ? 'items-center gap-2 px-1' : 'flex-col items-center gap-2'}`}>
        <div
          className="w-9 h-9 rounded-pill bg-canvas shadow-soft grid place-items-center text-[11px] font-semibold text-ink-700 shrink-0"
          title={me?.email || me?.sub || ''}
        >
          {(me?.email || me?.sub || '?').slice(0, 1).toUpperCase()}
        </div>
        {expanded && (
          <div className="min-w-0 flex-1 text-xs text-ink-700 truncate" title={me?.email || me?.sub || ''}>
            {me?.email || me?.sub || ''}
          </div>
        )}
        <button
          onClick={signOut}
          className={`
            rounded-soft grid place-items-center text-ink-500 hover:text-ink-900 hover:bg-canvas transition-colors
            ${expanded ? 'w-9 h-9 shrink-0' : 'w-11 h-11'}
          `}
          title="Sign out"
          aria-label="Sign out"
        >
          <LogOut size={17} strokeWidth={1.75} />
        </button>
      </div>
    </aside>
  )
}
